// src/components/shared/LoadingState.tsx

'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { useState, useEffect } from 'react'

// Add useDelayedLoading hook
export function useDelayedLoading(isLoading: boolean, delay = 200): boolean {
  const [showLoading, setShowLoading] = useState(false)

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (isLoading) {
      timer = setTimeout(() => setShowLoading(true), delay)
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional client-side state update inside an effect (SSR-safe staging of derived UI state)
      setShowLoading(false)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isLoading, delay])

  return showLoading && isLoading
}

const loadingVariants = cva(
  'animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800',
  {
    variants: {
      variant: {
        card: 'h-full w-full min-h-[200px]',
        text: 'h-4 w-full',
        title: 'h-6 w-3/4',
        image: 'aspect-[16/9] w-full',
        avatar: 'h-10 w-10 rounded-full',
        button: 'h-10 w-32 rounded-md',
      },
      size: {
        sm: 'min-h-[100px]',
        md: 'min-h-[200px]',
        lg: 'min-h-[300px]',
      },
    },
    defaultVariants: {
      variant: 'card',
      size: 'md',
    },
  }
)

interface LoadingStateProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingVariants> {
  count?: number
  className?: string
  isLoading?: boolean
  delay?: number
}

export function LoadingState({
  variant,
  size,
  count = 1,
  className,
  isLoading = true,
  delay = 200,
  ...props
}: LoadingStateProps): JSX.Element | null {
  const shouldShow = useDelayedLoading(isLoading, delay)

  if (!shouldShow) return null

  return (
    <div className="space-y-4" {...props}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={cn(loadingVariants({ variant, size }), className)}
        />
      ))}
    </div>
  )
}

// Specialized loading components for common use cases
export function LoadingCard(props: Omit<LoadingStateProps, 'variant'>): JSX.Element {
  return <LoadingState variant="card" {...props} />
}

export function LoadingText(props: Omit<LoadingStateProps, 'variant'>): JSX.Element {
  return <LoadingState variant="text" {...props} />
}

export function LoadingTitle(props: Omit<LoadingStateProps, 'variant'>): JSX.Element {
  return <LoadingState variant="title" {...props} />
}

export function LoadingImage(props: Omit<LoadingStateProps, 'variant'>): JSX.Element {
  return <LoadingState variant="image" {...props} />
}

export function LoadingAvatar(props: Omit<LoadingStateProps, 'variant'>): JSX.Element {
  return <LoadingState variant="avatar" {...props} />
}

export function LoadingButton(props: Omit<LoadingStateProps, 'variant'>): JSX.Element {
  return <LoadingState variant="button" {...props} />
}

// Common loading layouts
export function LoadingArticle(): JSX.Element {
  return (
    <div className="space-y-4">
      <LoadingTitle />
      <LoadingText count={3} />
      <LoadingImage />
      <LoadingText count={2} />
    </div>
  )
}

const LG_GRID_COLS: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  6: "lg:grid-cols-6",
  12: "lg:grid-cols-12",
}

export function LoadingGrid({ columns = 3 }: { columns?: number }): JSX.Element {
  return (
    <div className={cn(
      "grid grid-cols-1 gap-6 sm:grid-cols-2",
      LG_GRID_COLS[columns] ?? "lg:grid-cols-3"
    )}>
      <LoadingCard count={columns} />
    </div>
  )
}

export function LoadingProfile(): JSX.Element {
  return (
    <div className="flex items-center space-x-4">
      <LoadingAvatar />
      <div className="space-y-2">
        <LoadingTitle />
        <LoadingText />
      </div>
    </div>
  )
} 